import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../redux/store";
import axiosInstance from "../services/axiosInstance";
import { AuthState, getCurrentUser } from "../redux/slices/authSlice";
import { AuthService } from "../services/authService";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import Loader from "../components/common/Loader";

type AuthGuardProps = {
  children: React.ReactNode;
};

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const [showTokenExpiryModal, setShowTokenExpiryModal] =
    useState<boolean>(false);
  const isRefreshing = useRef<boolean>(false);
  const failedQueue = useRef<Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
  }>>([]);

  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { currentUser, loading } = useSelector<unknown, AuthState>(
    (state: any) => state.auth
  );

  const processQueue = (error: unknown = null) => {
    failedQueue.current.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });
    failedQueue.current = [];
  };

  useEffect(() => {
    const axiosInterceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          if (isRefreshing.current) {
            // Si un refresh est déjà en cours, ajouter la requête à la file d'attente
            return new Promise((resolve, reject) => {
              failedQueue.current.push({ resolve, reject });
            })
              .then(() => axiosInstance(originalRequest))
              .catch((err) => Promise.reject(err));
          }

          originalRequest._retry = true;
          isRefreshing.current = true;

          try {
            // Tenter de rafraîchir le token
            const response = await AuthService.refreshToken();

            if (response && response.status !== -1) {
              // Refresh réussi, traiter la file d'attente et réessayer la requête
              processQueue();
              isRefreshing.current = false;
              return axiosInstance(originalRequest);
            } else {
              // Status -1, afficher le modal
              processQueue(error);
              isRefreshing.current = false;
              setShowTokenExpiryModal(true);
              return Promise.reject(error);
            }
          } catch (refreshError: any) {
            // Erreur lors du refresh (status -1 ou autre erreur)
            processQueue(refreshError);
            isRefreshing.current = false;
            setShowTokenExpiryModal(true);
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    if (!currentUser) {
      dispatch(getCurrentUser());
    }

    return () => {
      axiosInstance.interceptors.response.eject(axiosInterceptor);
    };
  }, [currentUser, dispatch, navigate]);

  const handleLoginRedirect = () => {
    sessionStorage.setItem("session_expired", "true")
    navigate("/auth",{ replace: true }); 
    window.location.reload(); 
    setShowTokenExpiryModal(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><Loader/></div>;
  }


  return (
    <div className="w-full">
      {children}
      <Dialog
        visible={showTokenExpiryModal}
        modal
        onHide={() => {}}
        content={({ hide }) => (
          <div className="bg-white rounded-md p-4 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <i className="pi pi-exclamation-triangle text-primary-400"></i>
              <span className="text-black">Session Expired</span>
            </div>
            <p>Your session has expired. Please log in again to continue.</p>
            <Button
              label="Go to Login"
              onClick={handleLoginRedirect}
              className="bg-primary-500 text-white self-end hover:bg-primary-600"
            />
          </div>
        )}
      ></Dialog>
    </div>
  );
};

export default AuthGuard;
